(ns graph-ui.web.bind
  "CLJS port of ui.bind binding runtime (PLAN-graph-ui-v0-001-02 Phase W4).

   The server-side binding (src/ui/bind.clj) resolves :bind slots by reading
   in-process sources via the bus. The web renderer resolves :bind slots by
   dispatching :ui/read over the WebSocket bus — the same query, different
   transport. The description names the source; the bus provides the data.

   Also ports fill-command (the input→command seam) for Phase W5."
  (:require [clojure.walk :as walk]
            [clojure.string :as str]
            [graph-ui.web.generated-meta-ui :as gmu]))

;; @feature F-gi-v3-010-01-graph-ui-poc
;; @adr ADR-graph-ui-v0-003-01-ui-from-formal-description

(defn- bind-spec
  "Normalise a :bind value to {:source kw :args map}."
  [b]
  (if (keyword? b) {:source b} b))

(defn at-attach?
  "Is this pair read at attach, or left for the widget that draws it?
   MIRRORS ui.bind/at-attach? — the two binding runtimes are independent
   implementations of one contract (CLAUDE.md), so a change to one is not a
   change to the other. A pair with no :load is read at attach, which is what
   every bind did before PLAN-ikc-v3-033-01 step 4a."
  [pair]
  (not= :on-visible (:load pair)))

(def ^:private content-prop
  "Which prop a bound value fills, per widget kind — mirrors ui.bind/content-prop."
  {:text :value :list :items :tree :items :outline :items
   :code-block :value :markdown :value :input :value
   ;; F-graph-ui-v0-011-01: a bound :code-view fills its BODY. Mirrors
   ;; ui.bind/content-prop exactly — the two runtimes are independent
   ;; implementations of one contract and a fix to either is not a fix to both.
   :code-view :value
                             ;; A bound :image fills its src (phase 11).
                             :image :value
   :text-area :value :tab-set :active
   ;; widget wave (2026-07-14): a bound :select fills its option list, :table
   ;; its rows, :menu its action items. Mirrors ui.bind/content-prop exactly.
   :select :options :table :rows :menu :items
   ;; F-platform-v0-011-01: the highlight gutter's row list. Mirrors
   ;; ui.bind/content-prop exactly — see the note there.
   :highlight-list :items
   ;; PLAN-ikc-v3-015-01 phase 6: a bound :voice fills its :items — the entries
   ;; it may speak. Mirrors ui.bind/content-prop exactly.
:voice :items
   ;; ADR-graph-ui-v0-026-01 phase 2: a bound :dock fills its :arrangement —
   ;; the person's own layout for this surface and the panes it was made for,
   ;; or nil when they have not diverged from the declared default. MIRRORED in
   ;; the other bind runtime for the reason stated above: the two are
   ;; independent implementations of one contract, so a kind added to one map
   ;; and not the other binds on one renderer and silently not on the other.
   :dock :arrangement
   ;; PLAN-ikc-v3-024-01 M8 O10 / track R: a bound :graph fills its :subgraph.
   ;; MIRRORED from ui.bind/content-prop, for the reason stated there — a kind
   ;; added to one map and not the other binds on one renderer and silently not
   ;; on the other.
   :graph :subgraph})

(defn per-prop-bind?
  "Is this :bind the PER-PROP form — {prop {:source …} …} — rather than the
  single content-prop form (`:source-kw` or `{:source … :args …}`)?
  Distinguished by the absence of :source at the top level, since a per-prop
  map's keys are PROP NAMES. MIRRORS ui.bind/per-prop-bind? exactly — the two
  bind runtimes are independent implementations of one contract (CLAUDE.md), so
  a change here needs the same change there.

  WHY (2026-07-19): :bind used to fill exactly ONE privileged content-prop per
  widget kind, so a :select could bind :options or :value but never both — a
  dropdown could not show which of its own options was selected. Every
  mainstream framework binds props by name; this removes an artificial
  restriction rather than adding a UI idea."
  [b]
  (and (map? b) (nil? (:source b))))

(defn- bind-specs
  "Every {:source :args} spec a bind-like value carries — one for the
  content-prop forms, N for the per-prop form."
  [b]
  (if (per-prop-bind? b) (mapv bind-spec (vals b)) [(bind-spec b)]))

(declare scope-of tile-scopes tile-sessions-symbol)


(defn- mapcat-indexed
  "mapcat with the child's index, which is what a :tab-set needs to know which
   panel is the active one."
  [f coll]
  (apply concat (map-indexed f coll)))

(defn hides-child?
  "Is this node's child number `i` off the screen at first paint?
   MIRRORS ui.bind/hides-child? — a :tab-set draws only its active panel's
   content, and a collapsed :collapsible places none of its children. Anything
   already hidden stays hidden.

   A child on the line of the tabs (:section :left-section or :right-section)
   is never hidden, and :active counts the tabs only
   (IKC3/PLAN-ikc-v3-068-01/T)."
  [node i hidden?]
  (boolean
   (or hidden?
       (and (= :tab-set (:widget node))
            (let [kids  (vec (:children node))
                  line? #(contains? #{:left-section :right-section} (:section %))]
              (and (not (line? (get kids i)))
                   (not= (count (remove line? (take i kids))) (or (:active node) 0)))))
       (and (= :collapsible (:widget node)) (:collapsed node)))))

(defn bound-nodes
  "Every {:source :args} bind spec in a description tree, depth-first —
  :bind (which may bind several props at once, see per-prop-bind?) and
  :link-bind (an optional SECOND bind resolved into the fixed :links prop —
  see resolve-bindings).

  THE TWO-ARITY APPLIES `substitutions` to each :args and honours a subtree's
  own scope, so a tile's binds are subscribed for the TILE's session. A subtree
  whose :session-of names no tile yields nothing.

  MIRRORS ui.bind/bound-nodes, including the shape of one spec: :args is present
  only when the bind carries one."
  ([node] (bound-nodes node {} false))
  ([node substitutions] (bound-nodes node substitutions false))
  ([node substitutions hidden?]
   (let [subs (scope-of substitutions node)]
     (when-not (= :absent subs)
       (let [plain (dissoc subs tile-sessions-symbol)
             ;; :load RIDES WITH THE PAIR when the bind declares one
             ;; (PLAN-ikc-v3-033-01 step 4a). Added only when present, so every
             ;; pair that predates this keeps its exact shape. MIRRORS
             ;; ui.bind/bound-nodes.
             fill  (fn [{:keys [source args load]}]
                     (cond-> {:source source}
                       args (assoc :args (if (seq plain)
                                           (walk/postwalk-replace plain args)
                                           args))
                       load (assoc :load load)
                       ;; A KIND CARRIES :on-visible TO ITS CHILDREN (step 4b).
                       ;; MIRRORS ui.bind/bound-nodes, and the two must agree —
                       ;; the server decides what to send by this rule and the
                       ;; client decides what to ask for by the same one.
                       (and hidden? (nil? load)) (assoc :load :on-visible)))]
         (concat (when-let [b (:bind node)] (map fill (bind-specs b)))
                 (when-let [b (:link-bind node)] (map fill (bind-specs b)))
                 ;; A :per-row node subscribes the source its rows come from.
                 (when-let [b (:per-row node)] (map fill (bind-specs b)))
                 ;; A :per-tile node subscribes its template once per tile, so a
                 ;; change in tile 4 refreshes tile 4.
                 (if (:per-tile node)
                   (mapcat (fn [tile-subs]
                             (mapcat #(bound-nodes % tile-subs hidden?) (:children node)))
                           (tile-scopes subs))
                   (mapcat-indexed
                    (fn [i child] (bound-nodes child subs (hides-child? node i hidden?)))
                    (:children node)))))))))

(defn canon-uuid
  "Normalise a UUID-ish value to canonical 8-4-4-4-12 zero-padded form, so two
  spellings of the SAME id are ONE value — the client's own '53-3-4-8-4bc' and
  the server's zero-padded '00000053-0003-0004-0008-0000000004bc'. A value
  without exactly five hyphen groups is returned as a string unchanged, so a
  non-UUID argument still compares by its own text.

  IT LIVES HERE NOW, and it moved on 2026-08-22 for a reason. It was
  graph-ui.web.core/canon-uuid, used only by `same-session?` to COMPARE two
  spellings. Once the snapshot key carries the arguments
  (ADR-graph-ui-v0-023-01), the same two spellings become two KEYS, and a value
  written under one cannot be read under the other. So the key rule and the
  identity rule are one rule and one function. T-web-src-003 caught this by
  failing: a page click whose event carried the server-padded id stopped
  reaching the pane."
  [x]
  (when (some? x)
    (let [parts (str/split (str x) #"-")]
      (if (= 5 (count parts))
        (str/join "-"
          (map (fn [w p] (str (apply str (repeat (max 0 (- w (count p))) "0")) p))
               [8 4 4 4 12] parts))
        (str x)))))

(defn- key-arg
  "One argument value, as the KEY sees it. A uuid-ish value is canonicalised, so
  the id's spelling cannot split one entry into two. Anything else is itself."
  [v]
  (if (or (uuid? v) (and (string? v) (= 5 (count (str/split (str v) #"-")))))
    (canon-uuid v)
    v))

(defn snapshot-key
  "PURE: the key one bind's value is stored under in the source snapshot —
  `[source args]` when the bind carries arguments, the bare `source` keyword
  when it does not.

  MIRRORS ui.bind/snapshot-key EXACTLY, and here the mirror is load-bearing in a
  way most of this file's mirrors are not: the SERVER writes the map and this
  file READS it. A disagreement is not two renderers behaving differently, it is
  a value that cannot be found at all.

  WHY (ADR-graph-ui-v0-023-01): a description may bind ONE source twice for two
  different things, which is what a reading pane of several tiles IS. Keyed by
  the source alone, the reads collapse and every tile draws the last answer.

  An empty argument map keys as the bare keyword, because
  dsl/alatus/universal-sidebar.edn writes `:args {}` for a source that takes
  nothing."
  [source args]
  (if (seq args)
    [source (into {} (map (fn [[k v]] [k (key-arg v)])) args)]
    source))

(def tile-sessions-symbol
  "The substitution key that carries the pane's tile sessions. MIRRORS
  ui.bind/tile-sessions-symbol."
  '$tile-sessions)

(def focused-session-symbol
  "The substitution key carrying the session of the FOCUSED tile. A caller puts
   the :tile-focus-session source's answer here, and `:session-of :focused` reads
   it. MIRRORED across the two bind runtimes."
  '$focused-session)

(defn reply-failed?
  "PURE: did the bus refuse this command?

   A reply is a map, and a refusal carries :error. Anything else is a success —
   including nil, which is what a handler that returns no events sends back.

   IT IS ITS OWN FUNCTION because three callers ask the same question and one of
   them used to assume the answer (PLAN-ikc-v3-018-01 phase 1)."
  [reply]
  (let [m (js->clj reply :keywordize-keys true)]
    (boolean (and (map? m) (:error m)))))

(defn bake-scope
  "PURE: a node with the scoped substitutions BAKED INTO ITS GESTURES.

   WHY THIS IS NOT OPTIONAL. A bind is resolved when the tree is built, and a
   GESTURE is filled when a person clicks — by which time the renderer only
   knows the tab's own session. So a control inside a tile would send the TAB's
   session and act on the wrong thing.

   MEASURED IN THE BROWSER, 2026-08-23: four tiles drew correctly, each with its
   own transcript and its own prompt box, and the maximize control did nothing at
   all. It was sending the tab's session, and no tile answers to that. The four
   prompt boxes would have talked to one conversation.

   SO THE TILE'S SESSION IS WRITTEN INTO THE GESTURE while the tree is built,
   where the scope is known. `$value` is left alone: it is the reader's own
   input and belongs to click time.

   MIRRORED across the two bind runtimes."
  [node substitutions]
  (let [subs (dissoc substitutions tile-sessions-symbol focused-session-symbol)]
    (if (empty? subs)
      node
      (reduce (fn [n k]
                (if (contains? n k)
                  (assoc n k (walk/postwalk-replace subs (get n k)))
                  n))
              node
              [:on-gesture :on-submit]))))

(defn tile-scopes
  "PURE: one substitution map per OPEN tile, in order — what a `:per-tile` node's
   template is resolved against, once for each copy.

   Empty when the island has no tile. A `:per-tile` node then draws nothing,
   which is correct: there is no tile to draw.

   MIRRORED across the two bind runtimes."
  [substitutions]
  (let [sessions (remove #(= "" (str %)) (get substitutions tile-sessions-symbol))]
    (if (seq sessions)
      (mapv (fn [sid] (assoc substitutions '$session-id sid)) sessions)
      ;; NO TILE MEANS ONE COPY, FOR THE PANE ITSELF. This is what lets one
      ;; declaration serve both cases: a pane with no tile is a pane of one, and
      ;; :tile-columns answers 1, so it draws exactly the single column it always
      ;; drew. The alternative was two arms in a :tab-set, and the identity gate
      ;; refused it — two placements of one part duplicate every :import-id in
      ;; the graph, and the store upserts by :import-id.
      [substitutions])))

(defn scope-of
  "PURE: the substitutions in force INSIDE `node`.

  A node with `:session-of n` resolves against the nth TILE's session instead of
  the tab's. That is what makes a tile a view: the reading column under it binds
  `$session-id` and gets the tile's answer, so no source is copied per tile.

  Three answers. No `:session-of` gives the substitutions unchanged. An index
  with a tile gives `$session-id` rebound. An index with NO tile gives
  `:absent`, and the caller must then draw nothing — a grid of six cells over a
  pane of two tiles must show two, and a cell resolved against the tab's session
  would silently show the wrong thing.

  With no tile sessions supplied, `:session-of` is ignored.

  MIRRORS ui.bind/scope-of. These two runtimes are independent implementations
  of one contract (CLAUDE.md)."
  [substitutions node]
  (let [n (:session-of node)]
    (cond
      (nil? n) substitutions
      (not (contains? substitutions tile-sessions-symbol)) substitutions
      :else
      (let [sessions (vec (get substitutions tile-sessions-symbol))
            sid      (cond
                       ;; :focused — the tile the pane has the focus on. The
                       ;; reading pane uses this: it draws ONE column, and that
                       ;; column follows the focus.
                       (= :focused n) (get substitutions focused-session-symbol)
                       (number? n)    (when (< -1 n (count sessions)) (nth sessions n)))]
        (cond
          (and sid (not= "" (str sid))) (assoc substitutions '$session-id sid)
          ;; :focused WITH NO FOCUS IS THE PANE ITSELF, and this is what keeps
          ;; every existing island working. A pane holds no tile until a person
          ;; opens one, and the reading column must still draw. It is the same
          ;; rule ui.panes/pane-session states server-side: no tiles, so the
          ;; pane answers for itself.
          (= :focused n) substitutions
          ;; AN INDEX WITH NO TILE IS STILL :absent. A grid cell for a tile that
          ;; does not exist must vanish, not fall back to the pane and show the
          ;; wrong item.
          :else :absent)))))

(defn- lookup
  "One bind spec's value out of the snapshot. `substitutions` is applied to the
  spec's :args FIRST, because a description carries `$session-id` as a symbol and
  the server keyed the map with the real id substituted in. Without this the key
  built here can never match the key written there."
  [sources spec substitutions]
  (let [{:keys [source args]} (bind-spec spec)
        args (if (and args (seq substitutions))
               (walk/postwalk-replace substitutions args)
               args)]
    (get sources (snapshot-key source args))))

(defn- answered?
  "Has `sources` a real entry for this bind spec, even one whose value IS nil?

  `flush-reads!` (graph-ui.web.core) writes every reply with `assoc-in`, so a
  source that legitimately answers nil (:dock's :arrangement when a person has
  not diverged from the default, an empty :highlight-list) still leaves its KEY
  present. Plain `get`/`nil?` cannot tell that apart from a key `sources` has
  never held — both read as nil. A deferred bind's spec keeps :load :on-visible
  for its whole life once the tree carries it (`deferred?` above), so once the
  real, empty answer arrives, a `nil?` test marks the widget :stalled for ever,
  though the current answer is a legitimate, empty one."
  [sources spec substitutions]
  (let [{:keys [source args]} (bind-spec spec)
        args (if (and args (seq substitutions))
               (walk/postwalk-replace substitutions args)
               args)]
    (contains? sources (snapshot-key source args))))


;; ---------------------------------------------------------------------------
;; A TREE ROW IS A DECLARED SHAPE (PLAN-ikc-v3-033-01 step 1f).
;;
;; THE MIRROR OF ui.bind's OWN CHECK, and the two are independent
;; implementations of one contract (CLAUDE.md). A row that carries a key the
;; :tree kind does not declare is a row the renderer draws the known part of,
;; while the rest rides the wire. An outline row once carried the node's BODY:
;; 1,418 rows per attach, and 516 kB of one sidebar branch (measured 2026-09-19).
;;
;; THE TWO SIDES REFUSE WITH DIFFERENT FORCE, AND THAT IS DELIBERATE. ui.bind
;; throws, because it runs in the server, in the TUI and in the suite — the
;; three places where an author is the reader. Here the reader is a person with
;; a sidebar, and a thrown error inside a render blanks it. STALL, NEVER VANISH:
;; the rows are drawn, and the refusal is written to the console once.
;; ---------------------------------------------------------------------------

(defn dev-mode?
  "True when this bundle is asked to report what a reader would not be shown.
   The dev watch stamps the build id \"dev\", and window.IKC_DEV forces it on
   a release bundle."
  []
  (boolean (or (and (exists? js/window) (.-IKC_DEV js/window))
               (= "dev" (some-> (exists? js/window) (and js/window.IKC_BUILD_ID))))))

(defn- schema-map-keys
  "The keys of the [:map …] inside a malli schema, whatever it is nested in."
  [schema]
  (when (vector? schema)
    (if (= :map (first schema))
      (set (keep #(when (vector? %) (first %)) (rest schema)))
      (some schema-map-keys (filter vector? schema)))))

(defn- prop-schema
  "The schema of one prop inside a kind's [:map …] props block. The kind's own
   props are ALSO a map, so a naive walk answers :tone and :density."
  [props prop]
  (some (fn [e] (when (and (vector? e) (= prop (first e))) (last e)))
        (rest props)))

(def row-shape
  "The keys a :tree row may carry, read from the generated mirror of ui.edn —
   the same declaration ui.bind reads on the server."
  (memoize
   (fn []
     (let [m (:versions gmu/meta-ui-versions)
           v (get m (:latest gmu/meta-ui-versions))]
       (some-> (get-in v [:rules :tree :props]) (prop-schema :items) schema-map-keys)))))

(defonce ^:private reported (atom #{}))

(defn- report-stray-row-keys!
  "Write one console refusal for a :tree row carrying an undeclared key. Once
   per source and key set, so a repainting tree does not fill the console."
  [source rows]
  (when (and (dev-mode?) (sequential? rows) (every? map? rows))
    (when-let [allowed (row-shape)]
      (let [stray (into (sorted-set) (remove allowed (mapcat keys (take 200 rows))))]
        (when (and (seq stray) (not (contains? @reported [source stray])))
          (swap! reported conj [source stray])
          (js/console.error
           (str "REFUSED ROW SHAPE — the source " source " answers a :tree row with "
                (pr-str (vec stray)) ". The :tree kind declares only "
                (pr-str (vec (sort allowed)))
                ". Answer the other facts from a second source, bound by the"
                " widget that draws them (PLAN-ikc-v3-033-01 step 1f).")))))))

;; ---------------------------------------------------------------------------
;; A BRANCH FETCHED ON DEMAND, PUT BACK WHERE IT BELONGS
;; (PLAN-ikc-v3-033-01 step 4c, Meta-UI v2.1's :children-query).
;;
;; A tree bound with :branch :root holds roots only. Each branch a person opens
;; is read separately and lands in the snapshot under its OWN key — the same
;; source, with the branch added to the arguments. These two functions put those
;; answers back into the tree the widget draws.
;;
;; PURE, AND IN BOTH BINDING RUNTIMES. The browser is where they run; the server
;; carries them so the two cannot drift, which is this project's rule for the
;; two implementations of one contract.
;; ---------------------------------------------------------------------------

(defn loaded-branches
  "Every branch of `spec`'s source already in `sources`, as {branch rows}.

   A KEY IS [source args], so a branch read differs from the root read only by
   the argument that carries it. Nothing else in the snapshot can match, because
   no other bind adds that argument."
  [sources spec branch-arg]
  (let [src (:source spec)
        arg (or branch-arg :branch)]
    (into {}
          (keep (fn [[k v]]
                  (when (and (vector? k) (= src (first k)) (map? (second k)))
                    (when-let [b (get (second k) arg)]
                      [b v]))))
          sources)))

(defn node-key
  "The name a tree row is known by, as a string.

   ONE RULE, AND IT LIVES HERE BECAUSE THREE COPIES OF IT CAUSED A DEFECT. A
   row is keyed by its :value, or by its LABEL when it has none. widgets
   /node-value is what Mantine holds, splice-branches is what a fetched branch
   rejoins by, and ui.notes-web/node-key is what the server answers. All three
   must agree, and on 2026-09-20 they did not.

   ALMOST EVERY BRANCH OF THE READER TREE CARRIES NO :value. A value makes the
   row clickable, so a partition or a month folder would select a page that
   names no frame. So the label is the key, and a rule that reads :value alone
   finds nothing and leaves the branch stalled for ever."
  [it]
  (let [v (:value it)]
    (cond
      (string? v)  v
      (keyword? v) (name v)
      (some? v)    (str v)
      :else        (str (:label it)))))

(defn splice-branches
  "`items` with each loaded branch's rows put under the row of that `node-key`.

   A BRANCH NOBODY HAS OPENED KEEPS :has-children AND NO CHILDREN, so it still
   draws a chevron and still asks when it is opened. A branch that answered
   NOTHING keeps neither, because it turned out to hold nothing — that is an
   answer, and it is the one case where a chevron must go."
  [items branch->rows]
  (mapv (fn [it]
          (let [rows (get branch->rows (node-key it) ::none)]
            (cond
              (= ::none rows)
              (cond-> it
                (seq (:children it))
                (assoc :children (splice-branches (:children it) branch->rows)))

              (seq rows)
              (-> it (dissoc :has-children)
                  (assoc :children (splice-branches rows branch->rows)))

              :else
              (-> it (dissoc :has-children :children)))))
        (or items [])))

(defn- with-branches
  "`v` with every branch a person opened put back under its own row.

   ONE HELPER AND TWO CALL SITES, because it was written at one of them. A tree
   may bind its rows through a WHOLE-prop :bind or through a per-prop one, and
   the reader sidebar uses per-prop: its rows and its highlight are two sources
   with two lifetimes (PLAN-ikc-v3-019-01 phase 4). The splice lived in the
   whole-prop arm alone, so every branch of the reader opened onto a
   placeholder that waited for ever, while the test passed on a whole-prop
   tree (2026-09-20).

   IT SPLICES THE PROP THE QUERY NAMES AND NO OTHER. :active-value is bound
   from a second source in the same map, and it is not a list of rows."
  [node spec v sources]
  (let [cq (:children-query node)]
    (if (and cq
             (= :tree (:widget node))
             (sequential? v)
             (= (:source cq) (:source spec)))
      (splice-branches v (loaded-branches sources spec (:branch-arg cq)))
      v)))

(defn- stall-mark
  "The pair a stalled widget asks for: the source, and its args substituted."
  [spec substitutions]
  {:source (:source spec)
   :args   (when-let [a (:args spec)]
             (if (seq substitutions) (walk/postwalk-replace substitutions a) a))})

(defn- deferred?
  "Is this bind one the attach did NOT send, so its widget must ask?

   THE SAME RULE `bound-nodes` APPLIES, and it has to be. The server derives
   :load while it walks, and it puts the key on the PAIR it keeps, never on the
   description it sends. So a spec read out of a description carries no :load
   whatever happened, and a test of `(:load spec)` alone always answered
   at-attach. Every deferred bind then drew empty and asked for nothing.

   MEASURED ON THE DEPLOYMENT, 2026-09-20: of 37 subscribed pairs
   :page-markdown was not one, and no frame ever named it. The reading pane
   drew nothing and the right gutter drew only its level pickers, which is
   what George reported.

   AN EXPLICIT :load STILL WINS, as it does in bound-nodes."
  [spec hidden?]
  (or (= :on-visible (:load spec))
      (and hidden? (nil? (:load spec)))))

(defn- resolve-one
  "Resolve ONE bind-like key (:bind or :link-bind) on `node` into `prop-key`
  from the `sources` snapshot map. `node` is first so this threads with ->.

  A PER-PROP :bind ignores `prop-key` and fills each named prop from its own
  source — how a :select binds :options AND :value at once."
  [node bind-key prop-key sources substitutions hidden?]
  (if-let [bnd (get node bind-key)]
    (if (per-prop-bind? bnd)
      ;; A PER-PROP BIND STALLS TOO, and it did not until 2026-09-20. The
      ;; reading pane binds :value, :highlights and :refused-tags on one
      ;; widget, and :value is the document. One waiting prop is enough to
      ;; stall the widget, because it cannot draw what it has not got.
      (let [filled (reduce (fn [n [prop spec]]
                             (let [spec (bind-spec spec)]
                               (assoc n prop (with-branches node spec
                                               (lookup sources spec substitutions)
                                               sources))))
                           (dissoc node bind-key)
                           bnd)
            waiting (->> bnd
                         (keep (fn [[prop spec]]
                                 (let [spec (bind-spec spec)]
                                   (when (and (not (answered? sources spec substitutions))
                                              (deferred? spec hidden?))
                                     spec))))
                         first)]
        (cond-> filled
          (and waiting (nil? (:stalled filled)))
          (assoc :stalled (stall-mark waiting substitutions))))
      (let [spec (bind-spec bnd)
            v0   (lookup sources bnd substitutions)
            ;; A TREE THAT LOADS ITS BRANCHES ON DEMAND GETS THEM BACK HERE
            ;; (step 4c). Each branch a person opened was read under its own
            ;; key, and this is where those answers rejoin the tree the widget
            ;; draws. A tree with no :children-query is untouched.
            v    (with-branches node spec v0 sources)]
        (when (= :tree (:widget node))
          (report-stray-row-keys! (:source spec) v))
        ;; A LAZY BIND THAT HAS NOT ANSWERED YET SAYS SO (step 4a). The renderer
        ;; draws a waiting mark for :stalled, and the widget asks for the value
        ;; when it mounts. Without the mark an unarrived list and an empty list
        ;; look identical, and a reader believes the empty one.
        ;; A MARK ALREADY SET STAYS (2026-09-25, lane RP). resolve-one runs for
        ;; :bind and then for :link-bind, and the second mark replaced the
        ;; first. So the conversation body asked for its LINKS before its text.
        ;; The first waiting bind is the widget's content, and it is asked
        ;; first; graph-ui.web.render asks for the next when the mark moves.
        (cond-> (dissoc node bind-key)
          (and (nil? (:stalled node))
               (not (answered? sources spec substitutions)) (deferred? spec hidden?))
          (assoc :stalled (stall-mark spec substitutions))
          prop-key (assoc prop-key v))))
    node))

(defn resolve-of
  "PURE: collapse every [:of m k] whose m is a MAP into (get m k). `k` may be
  a symbol — a description writes [:of $value parent-id] unquoted — or a
  keyword. A vector whose second element is not yet a map is left as it is,
  so a template keeps its references until the symbol in them is substituted.
  MIRRORS ui.bind/resolve-of."
  [form]
  (walk/postwalk
    (fn [x]
      (if (and (vector? x) (= 3 (count x)) (= :of (first x)) (map? (second x)))
        (let [k (nth x 2)]
          (get (second x) (if (symbol? k) (keyword (name k)) k)))
        x))
    form))

(defn resolve-bindings
  "Walk a description; for each bound node, read its source value from the
  `sources` map (a snapshot from :ui/attach or accumulated :ui/read results)
  and inject the value into the widget's content prop (text→:value, list→:items),
  dropping :bind. A node may ALSO carry :link-bind — a SECOND, independent
  bind resolved into the fixed :links prop (today only :code-block's
  inline-annotation-links use it). The result is a plain Actual-UI
  description the interpreter renders.

  `sources` is the source snapshot from :ui/attach, updated incrementally on
  :ui/source-changed. Its keys are `snapshot-key`s — a bare source keyword for a
  bind with no arguments, `[source args]` for one that carries them.

  `substitutions` ({'$session-id id '$tile-sessions [...]}) is applied to a
  bind's :args before the key is built. A subtree may narrow it: see `scope-of`.
  A node whose `:session-of` names no tile resolves to NIL and its parent drops
  it. IT WAS ACCEPTED AND IGNORED HERE UNTIL 2026-08-22: the argument was
  threaded to every child and never read, because one value per source name
  needed no arguments to find it. ADR-graph-ui-v0-023-01 made the key carry the
  arguments, so this is now the difference between finding a value and not."
  ([sources node] (resolve-bindings sources node {} false))
  ([sources node substitutions] (resolve-bindings sources node substitutions false))
  ;; `hidden?` SAYS THIS NODE HAS NO PIXEL AT FIRST PAINT, and it is why the
  ;; fourth argument exists (2026-09-20). The walk carries it the same way
  ;; bound-nodes does, so both halves agree by construction rather than by a
  ;; key the description never carried.
  ([sources node substitutions hidden?]
   (let [subs (scope-of substitutions node)]
     (when-not (= :absent subs)
       (let [filled (-> node
                        (resolve-one :bind (content-prop (:widget node)) sources subs hidden?)
                        (resolve-one :link-bind :links sources subs hidden?)
                        (bake-scope subs))]
         (cond
           ;; A :per-row NODE DRAWS ITS TEMPLATE ONCE PER ROW OF A SOURCE, with
           ;; `$row` bound to the row and every [:of $row k] collapsed before the
           ;; copy is resolved (Meta-UI v2.1 :per-row, PLAN-ikc-v3-026-01). The
           ;; sibling of :per-tile below: one template in the graph, a count that
           ;; is data. A source that answers nothing draws nothing.
           (:per-row filled)
           (let [rows (lookup sources (:per-row filled) subs)]
             (assoc (dissoc filled :per-row)
                    :children
                    (vec (for [row   (if (sequential? rows) rows [])
                               child (:children filled)
                               :let  [r (resolve-bindings
                                          sources
                                          (resolve-of (walk/postwalk-replace {'$row row} child))
                                          subs hidden?)]
                               :when r]
                           r))))

           ;; A :per-tile NODE DRAWS ITS TEMPLATE ONCE PER OPEN TILE. The graph
           ;; holds ONE child — a frame is identified by its :import-id, and six
           ;; copies would collapse into one frame. The count is data, so the
           ;; declaration says "per tile" and never says how many
           ;; (ADR-graph-ui-v0-023-01).
           (:per-tile filled)
           (assoc (dissoc filled :per-tile)
                  :children
                  (vec (for [tile-subs (tile-scopes subs)
                             child     (:children filled)
                             :let [r (resolve-bindings sources child tile-subs hidden?)]
                             :when r]
                         r)))



           ;; A CHILD THAT RESOLVES TO NOTHING IS DROPPED, not kept as nil. Its
           ;; :session-of names a tile the pane does not have. `:narrow :hide` is
           ;; the precedent: this vocabulary already lets a declaration say
           ;; "not here".
           ;; EACH CHILD IS ASKED WHETHER THE ATTACH SENT ITS VALUES. hides-child?
           ;; is the same predicate bound-nodes uses: a :tab-set draws only its
           ;; active panel, and a collapsed :collapsible places none of its
           ;; children. Hidden only ever spreads downward.
           ;;
           ;; IT READS `node`, THE UNRESOLVED NODE, AND NEVER `filled`
           ;; (2026-09-25, lane RP). The server's attach and bound-nodes both
           ;; walk the DESCRIPTION, where a bound :tab-set has no :active and so
           ;; shows child 0. `filled` carries the RESOLVED :active. So when
           ;; :page-view answered 5, child 5 was "not hidden" here, its bind was
           ;; not :stalled, and nothing asked for :page-conversation. The pane
           ;; drew an empty conversation for ever. Two walks with two ideas of
           ;; "hidden" is the defect, and PR #67's day-stepper was one instance.
           ;; `hidden?` here means "the attach did not send it". That is one
           ;; fact, so it must come from the one tree both walks read.
           (:children filled)
           (update filled :children
                   #(vec (keep identity
                               (map-indexed
                                (fn [i c]
                                  (resolve-bindings sources c subs
                                                    (hides-child? node i hidden?)))
                                %))))

           :else filled))))))

(defn gesture-template
  "The command template for ONE NAMED gesture of a node — :on-gesture's own key
   (Meta-UI v2.x).

   submit-template below stays a separate function on purpose: :submit is the
   one gesture with a v1.x spelling (:on-submit) to fall back to, and reading
   both spellings for every gesture would invent v1.x keys that never existed."
  [props gesture]
  (get-in props [:on-gesture gesture]))

(defn submit-template
  "The command template for a node's PRIMARY gesture: :on-gesture's :submit
   key under Meta-UI v2.x, :on-submit under v1.x.

   IT LIVES HERE BECAUSE TWO RENDERERS ASKED THE SAME QUESTION AND ONE OF THEM
   GUESSED (2026-08-20). widgets.cljs resolved both spellings; prompt_area.cljs
   read :on-submit alone, so under v2.0 its Send button and its keys were both
   absent — the whole submit path of the dashboard prompt box was dead, and the
   description was correct the whole time. George found it by typing into it.
   One rule, one place, both callers."
  [{:keys [on-gesture on-submit]}]
  (or (:submit on-gesture) on-submit))

(defn fill-command
  "Build the bus command an :on-submit/:on-fork action dispatches. `substitutions`
  is either a plain value (substituted for $value) or a symbol→value map for
  multi-substitution (e.g. {'$value entry-id '$session-id sid}) — mirrors
  ui.bind/fill-command. PURE — the same input→command seam the TUI renderer
  and the integration test both call. {:command kw :with {…}} + text →
  {:type kw …text…}.

  A [:of $value key] in :with reads one field of a MAP $value after the
  substitution (Meta-UI v2.1's :tree gestures hand the selected item's map)."
  [on-submit substitutions]
  (let [{:keys [command with]} on-submit
        subst (if (map? substitutions) substitutions {'$value substitutions})]
    (assoc (resolve-of (walk/postwalk-replace subst (or with {})))
           :type command)))

(defn dragged-row
  "PURE: the row a drag carried, as a map with keyword keys, or nil when the
   drag carried no row. The row crosses as JSON, so its keys arrive as
   strings. MIRRORS ui.bind/dragged-row."
  [x]
  (when (map? x)
    (into {} (map (fn [[k v]] [(if (string? k) (keyword k) k) v])) x)))

(defn drop-command
  "PURE: the command a :drop gesture sends (IKC3/PLAN-ikc-v3-064-01/DRAG).
   `$dragged` in the template's :with is the dragged row's own map, as `$value`
   is the row's own map for a table's :submit, so [:of $dragged item-id] reads
   one key. `substitutions` holds the others, e.g. {'$session-id sid}.

   nil when the drag carried no row, or the template names no :command.
   MIRRORS ui.bind/drop-command. One table of cases holds both:
   test/cljs/graph_ui/web/fixtures/dragged_cases.edn."
  [template dragged substitutions]
  (when-let [row (dragged-row dragged)]
    (when (:command template)
      (fill-command template (assoc substitutions '$dragged row)))))
